import { useMemo } from "react";
import { Money } from "@shop-ledger/shared-types";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listInvoices } from "../../api/invoicesApi";
import { AGING_BUCKET_LABEL, AGING_BUCKET_ORDER, ageInvoices, summarizeAgingBuckets, type AgedInvoice } from "../../domain/aging";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const TENANT_CURRENCY_FALLBACK = "UGX"; // Tenant.currency isn't returned by GET /auth/me
// (UserOut, `@shop-ledger/shared-types`'s api/auth.ts) — same documented stand-in every
// other money-displaying page in this app uses (see e.g. DashboardPage.tsx).

const COLUMNS: DataTableColumn<AgedInvoice, string>[] = [
  { key: "contact", header: "Contact", sortable: true, render: (r) => r.invoice.contact_name },
  { key: "type", header: "Type", render: (r) => (r.invoice.type === "CUSTOMER_INVOICE" ? "Invoice" : "Bill") },
  { key: "due_date", header: "Due Date", sortable: true, render: (r) => r.invoice.due_date },
  { key: "days", header: "Days Past Due", sortable: true, align: "right", render: (r) => Math.max(0, r.daysPastDue) },
  { key: "balance", header: "Balance", align: "right", render: (r) => Money.format(r.balanceRemaining, TENANT_CURRENCY_FALLBACK) },
  { key: "bucket", header: "Bucket", render: (r) => AGING_BUCKET_LABEL[r.bucket] },
];

/**
 * §5's "explicit aging matrix for Pending Invoices/Bills". This is a **client-side
 * derived view**, documented per this app's task brief: there's no dedicated aging
 * endpoint (`backend/BACKEND_ARCHITECTURE.md`'s API surface table), so bucketing is
 * computed here from real `due_date`/`amount_due`/`amount_paid` fields off
 * `GET /invoices` (`domain/aging.ts`).
 */
export function AgingPage() {
  const { ensureFreshToken } = useAuth();
  const invoicesState = useAsyncData(() => ensureFreshToken().then((token) => listInvoices(token)), []);

  const aged = useMemo(() => ageInvoices(invoicesState.data ?? []), [invoicesState.data]);
  const totals = useMemo(() => summarizeAgingBuckets(aged), [aged]);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Aging</h1>
          <p className="page__subtitle">
            Outstanding invoices/bills bucketed by days past <code>due_date</code> — computed client-side from
            <code> GET /invoices</code>, not a dedicated backend report.
          </p>
        </div>
      </div>

      {invoicesState.error ? <StatusBanner kind="error">{invoicesState.error}</StatusBanner> : null}

      <div className="stat-row">
        {AGING_BUCKET_ORDER.map((bucket) => (
          <div className="stat-tile" key={bucket}>
            <p className="stat-tile__label">{AGING_BUCKET_LABEL[bucket]}</p>
            <p className={`stat-tile__value ${bucket === "CURRENT" ? "stat-tile__value--positive" : "stat-tile__value--negative"}`}>
              {Money.format(totals[bucket], TENANT_CURRENCY_FALLBACK)}
            </p>
          </div>
        ))}
      </div>

      <div className="panel">
        {invoicesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={aged}
            getRowId={(row) => row.invoice.id}
            emptyMessage="Nothing outstanding — every invoice/bill is fully paid."
            caption="Aging matrix"
          />
        )}
      </div>
    </div>
  );
}
